import type React from 'react'
import { Input } from '@/components/ui/input'

type CpfEmailInputProps = React.InputHTMLAttributes<HTMLInputElement>

const EMAIL_OR_LETTER_PATTERN = /[a-zA-Z@]/
const NUMERIC_PREFIX_PATTERN = /^([\d.-]+)(.*)$/
const CPF_PUNCTUATION_PATTERN = /[.-]/g
const CPF_MAX_DIGITS = 11
const CPF_MASK_NINE_DIGITS = 9
const CPF_MASK_SIX_DIGITS = 6
const CPF_MASK_THREE_DIGITS = 3
const CPF_FULL_MASK_PATTERN = /(\d{3})(\d{3})(\d{3})(\d{1,2})/
const CPF_NINE_MASK_PATTERN = /(\d{3})(\d{3})(\d{1,3})/
const CPF_SIX_MASK_PATTERN = /(\d{3})(\d{1,3})/

const stripCpfMaskFromEmailValue = (inputValue: string) => {
  const atIndex = inputValue.indexOf('@')
  const localPart = atIndex === -1 ? inputValue : inputValue.slice(0, atIndex)
  const domainPart = atIndex === -1 ? '' : inputValue.slice(atIndex)
  const prefixMatch = NUMERIC_PREFIX_PATTERN.exec(localPart)

  if (!prefixMatch) {
    return inputValue
  }

  const numericPrefix = (prefixMatch.at(1) ?? '').replace(
    CPF_PUNCTUATION_PATTERN,
    ''
  )
  return `${numericPrefix}${prefixMatch.at(2) ?? ''}${domainPart}`
}

const maskCpfValue = (inputValue: string) => {
  const rawValue = inputValue.replace(/\D/g, '').slice(0, CPF_MAX_DIGITS)

  if (rawValue.length > CPF_MASK_NINE_DIGITS) {
    return rawValue.replace(CPF_FULL_MASK_PATTERN, '$1.$2.$3-$4')
  }

  if (rawValue.length > CPF_MASK_SIX_DIGITS) {
    return rawValue.replace(CPF_NINE_MASK_PATTERN, '$1.$2.$3')
  }

  if (rawValue.length > CPF_MASK_THREE_DIGITS) {
    return rawValue.replace(CPF_SIX_MASK_PATTERN, '$1.$2')
  }

  return rawValue
}

export function CpfEmailInput(props: Readonly<CpfEmailInputProps>) {
  const handleMaskedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    const nextValue = EMAIL_OR_LETTER_PATTERN.test(inputValue)
      ? stripCpfMaskFromEmailValue(inputValue)
      : maskCpfValue(inputValue)

    if (nextValue === inputValue) {
      props.onChange?.(e)
      return
    }

    const nextEvent = {
      ...e,
      target: {
        ...e.target,
        value: nextValue,
      },
    }

    props.onChange?.(nextEvent as React.ChangeEvent<HTMLInputElement>)
  }

  return (
    <Input
      {...props}
      className={`rounded-sm border border-[#D4D4D4] bg-transparent text-sm placeholder:text-[#737373] focus:border-[#B663FF] focus:bg-[#FAFAFA] ${props.className || ''}`}
      onChange={handleMaskedChange}
      placeholder="E-mail ou CPF"
    />
  )
}
